import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocalSearchParams, useNavigation, useRouter } from "expo-router";
import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import { useUnistyles } from "react-native-unistyles";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { guardedPush } from "@src/services/navigation/guarded-push";
import { useUsersAndPermissionsData } from "@src/features/settings/hooks";
import { useSites } from "@src/features/cameras/hooks/useSites";
import { useCameras } from "@src/features/cameras";
import { resolveCameraName } from "@src/features/cameras/utils/camera-display-name";
import { formatCameraLabel } from "@src/utils/key-parser";
import {
  useIsTenantAdmin,
  useUserList,
  userListFiltersFromParams,
  userListFiltersToParams,
  type UserListFilters,
  type UserListFilterSite,
  type UserListFilterCamera,
  type UserListItem,
} from "@visioni/a3s-access/app";

const SEARCH_COMMIT_DELAY_MS = 400;

export function useUsersAndPermissionsPage() {
  const { theme } = useUnistyles();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const router = useRouter();
  const { headerLeft } = useHeaderBackButton();

  const { isTenantAdmin, isLoading: permissionsLoading, isError: permissionsError } = useIsTenantAdmin();
  const { isReady, isError } = useUsersAndPermissionsData();
  const { users } = useUserList();
  const { sites } = useSites();
  const { data: cameras } = useCameras();

  // Filters live in the URL (`?q=&role=&site=&camera=`) so a filtered list can be
  // shared or linked to, e.g. from a camera's "Users & permissions" row.
  const params = useLocalSearchParams<{ q?: string; role?: string; site?: string; camera?: string }>();
  const { role, siteId, cameraId, search: committedSearch } = userListFiltersFromParams(params);

  // Search is typed, so it renders from local state and is written to the URL
  // once typing pauses rather than on every keystroke.
  const [search, setSearch] = useState(committedSearch);
  const searchCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (searchCommitTimer.current) clearTimeout(searchCommitTimer.current);
    },
    [],
  );

  const filters: UserListFilters = useMemo(
    () => ({ search, role, siteId, cameraId }),
    [search, role, siteId, cameraId],
  );

  // `navigation.setParams` targets this screen's route. `router.setParams` targets
  // whichever screen is focused, so a search committed after the user has opened
  // Edit User would land on that screen instead.
  const setFilters = useCallback(
    (next: UserListFilters) => {
      setSearch(next.search);
      if (searchCommitTimer.current) clearTimeout(searchCommitTimer.current);
      if (next.search !== search) {
        searchCommitTimer.current = setTimeout(
          () => navigation.setParams(userListFiltersToParams(next)),
          SEARCH_COMMIT_DELAY_MS,
        );
      } else {
        navigation.setParams(userListFiltersToParams(next));
      }
    },
    [navigation, search],
  );

  const filterSites: UserListFilterSite[] = useMemo(
    () => (sites ?? []).map((s) => ({ id: String(s.siteId), name: s.siteName })),
    [sites],
  );

  const filterCameras: UserListFilterCamera[] = useMemo(() => {
    const options = (cameras ?? []).map((c) => {
      const cameraKey = `${c.key.asset}@${c.key.id}`;
      return {
        id: `${c.site.id}/${cameraKey}`,
        name: resolveCameraName(c, cameraKey),
      };
    });
    // A linked camera that isn't in the fleet list (a legacy camera, or the list
    // still loading) still needs an option, or the filter applies with nothing
    // selected and no way to clear it.
    if (cameraId && !options.some((o) => o.id === cameraId)) {
      const cameraKey = cameraId.substring(cameraId.indexOf("/") + 1);
      options.push({ id: cameraId, name: formatCameraLabel(cameraKey) || cameraKey });
    }
    return options;
  }, [cameras, cameraId]);

  const filteredUsers: UserListItem[] = useMemo(() => {
    let result = users;

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter((u) => u.name.toLowerCase().includes(q) || (u.email?.toLowerCase().includes(q) ?? false));
    }

    if (filters.role) {
      result = result.filter((u) => u.permissions.some((p) => p.role === filters.role));
    }

    if (filters.siteId) {
      const siteId = filters.siteId;
      result = result.filter(
        (u) => u.permissions.some((p) => p.role === "tenant_admin") || u.permissions.some((p) => p.siteId === siteId),
      );
    }

    if (filters.cameraId) {
      const cameraId = filters.cameraId;
      const cameraSiteId = cameraId.includes("/") ? cameraId.substring(0, cameraId.indexOf("/")) : undefined;
      result = result.filter(
        (u) =>
          u.permissions.some((p) => p.role === "tenant_admin") ||
          u.permissions.some((p) => p.cameraId === cameraId) ||
          (cameraSiteId &&
            u.permissions.some(
              (p) => p.siteId === cameraSiteId && (p.role === "site_manager" || p.role === "site_viewer"),
            )),
      );
    }

    return result;
  }, [users, filters]);

  const handleInviteUser = useCallback(() => {
    guardedPush("/settings/site-access/invite-user");
  }, []);

  const handleEditUser = useCallback((userId: string) => {
    guardedPush(`/settings/site-access/edit-user/${userId}` as never);
  }, []);

  useLayoutEffect(() => {
    if (!permissionsLoading && !permissionsError && !isTenantAdmin) {
      router.replace("/forbidden");
    }
  }, [permissionsLoading, permissionsError, isTenantAdmin, router]);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Users & Permissions",
      headerLeft,
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
    });
  }, [navigation, theme.colors.background, headerLeft]);

  return {
    permissionsLoading,
    permissionsError,
    isTenantAdmin,
    isReady,
    isError,
    filters,
    setFilters,
    filterSites,
    filterCameras,
    filteredUsers,
    handleInviteUser,
    handleEditUser,
  };
}
